import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, Download, KeyRound, PauseCircle, Plus, Search, X } from 'lucide-react';
import { Button, ConfirmDialog, EmptyState, ErrorCard, KpiCard, Pagination, SkeletonKpiStrip, SkeletonTable } from '@/components/ui';
import { DataTable, Grid, PageHeader, Section } from '@/components/layout/PageLayout';
import { LicenseStatusBadge } from '@/components/tenants/LicenseStatusBadge';
import { LicenseKeyRevealDialog } from '@/components/tenants/LicenseKeyRevealDialog';
import { useCreateLicense, useLicenses, useToggleLicenseEnabled } from '@/api/licenses';
import { useTenants } from '@/api/tenants';
import { getErrorMessage } from '@/lib/errors';
import { showToast } from '@/lib/toast';
import { downloadLicenseFile } from '@/lib/downloadLicenseFile';
import type { LicenseListItem } from '@/types/license';
import type { Tenant } from '@/types/tenant';

const PAGE_SIZE = 10;

// ── License form (full-page, mirrors TenantPage's list/form pattern) ───────

function LicenseForm({ tenants, onDone }: { tenants: Tenant[]; onDone: (licenseKey?: string) => void }) {
  const [tenantId, setTenantId] = useState('');
  const [endpointLimit, setEndpointLimit] = useState(10);
  const [expiryDate, setExpiryDate] = useState('');
  const { mutate, isPending, error } = useCreateLicense();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate(
      { tenantId, endpointLimit, expiryDate: new Date(expiryDate).toISOString() },
      { onSuccess: (result) => onDone(result.licenseKey ?? undefined) },
    );
  };

  const inputCls =
    'w-full px-3 py-2 text-sm border border-border-subtle rounded-md bg-surface-0 text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/50 transition-colors';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={() => onDone()} leadingIcon={<ArrowLeft className="h-4 w-4" />} />
          <div>
            <h1 className="text-xl font-semibold text-text-primary">New license</h1>
            <p className="text-sm text-text-tertiary">Issue an endpoint license to a tenant</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => onDone()}>Cancel</Button>
          <Button type="submit" variant="primary" size="sm" loading={isPending} leadingIcon={<KeyRound className="h-3.5 w-3.5" />}>
            Issue license
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs font-medium text-state-danger">
          {getErrorMessage(error)}
        </div>
      )}

      <div className="max-w-md rounded-lg border border-border-subtle bg-surface-1 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-text-primary">License details</h2>

        {tenants.length === 0 ? (
          <p className="text-xs text-text-tertiary">Every tenant already has a license.</p>
        ) : (
          <>
            <div className="space-y-1">
              <label className="block text-xs font-medium text-text-secondary">Tenant</label>
              <select
                className={inputCls}
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                required
                autoFocus
              >
                <option value="" disabled>Select a tenant…</option>
                {tenants.map((t) => (
                  <option key={t.tenantId} value={t.tenantId}>{t.name} ({t.tenantId})</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="block text-xs font-medium text-text-secondary">Endpoint limit</label>
              <input
                type="number"
                min={0}
                className={inputCls}
                value={endpointLimit}
                onChange={(e) => setEndpointLimit(Number(e.target.value))}
                required
              />
            </div>

            <div className="space-y-1">
              <label className="block text-xs font-medium text-text-secondary">Expiry date</label>
              <input
                type="date"
                className={inputCls}
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                required
              />
            </div>
          </>
        )}
      </div>
    </form>
  );
}

// ── Main page ────────────────────────────────────────────────────────────

export function LicensePage() {
  const navigate = useNavigate();
  const [view, setView] = useState<'list' | 'form'>('list');
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [togglingLicense, setTogglingLicense] = useState<LicenseListItem | null>(null);

  const { data, isLoading, isError, error, refetch } = useLicenses(page, PAGE_SIZE, search);
  // Unfiltered meta query (same convention as TenantPage/EndpointsPage) so KPI
  // totals reflect all licenses rather than just the current page.
  const { data: metaData, isLoading: metaLoading } = useLicenses(1, 500);
  // Tenants without a license yet — the only valid targets for "Issue license".
  const { data: tenantData } = useTenants(1, 500);
  const { mutate: toggleEnabled, isPending: isToggling } = useToggleLicenseEnabled();

  const handleSearch = (v: string) => { setSearch(v); setPage(1); };

  const licenses = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const allLicenses = metaData?.data ?? [];
  const activeCount = allLicenses.filter((l) => l.status === 'active').length;
  const expiringSoonCount = allLicenses.filter((l) => l.status === 'expiring_soon').length;
  const needsAttentionCount = allLicenses.filter((l) => l.status === 'expired' || l.status === 'disabled').length;
  const unlicensedTenants = (tenantData?.data ?? []).filter((t) => !t.license);

  const openCreate = () => setView('form');
  const closeForm = (licenseKey?: string) => {
    setView('list');
    if (licenseKey) setRevealedKey(licenseKey);
  };

  const handleDownload = (l: LicenseListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!l.licenseKey) {
      showToast('License key unavailable for download');
      return;
    }
    downloadLicenseFile(l.tenantId, l.licenseKey);
  };

  const handleToggleConfirm = () => {
    if (!togglingLicense) return;
    toggleEnabled(
      { tenantId: togglingLicense.tenantId, enabled: !togglingLicense.enabled },
      {
        onSuccess: () => {
          showToast(togglingLicense.enabled ? 'License disabled' : 'License enabled');
          setTogglingLicense(null);
        },
        onError: () => setTogglingLicense(null),
      },
    );
  };

  if (view === 'form') {
    return (
      <div className="p-1">
        <LicenseForm tenants={unlicensedTenants} onDone={closeForm} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Governance"
        title="Licenses"
        description="Endpoint licenses issued to tenants on this deployment server"
        actions={
          <Button variant="primary" size="sm" leadingIcon={<Plus className="h-3.5 w-3.5" />} onClick={openCreate}>
            Issue License
          </Button>
        }
      />

      {metaLoading ? (
        <SkeletonKpiStrip count={4} />
      ) : (
        <Grid cols={4}>
          <KpiCard label="Total licenses" value={allLicenses.length} variant="accent" icon={<KeyRound className="h-3.5 w-3.5" />} />
          <KpiCard label="Active" value={activeCount} variant="positive" />
          <KpiCard label="Expiring soon" value={expiringSoonCount} variant={expiringSoonCount > 0 ? 'warning' : 'neutral'} />
          <KpiCard
            label="Needs attention"
            value={needsAttentionCount}
            variant={needsAttentionCount > 0 ? 'warning' : 'neutral'}
            icon={<AlertTriangle className="h-3.5 w-3.5" />}
          />
        </Grid>
      )}

      {isError ? (
        <ErrorCard title="Failed to load licenses" message={getErrorMessage(error)} onRetry={() => void refetch()} />
      ) : (
        <Section title="All licenses">
          {/* Search bar */}
          <div className="mb-3 flex items-center gap-3">
            <div className="relative w-72">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
              <input
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by tenant ID, tenant name, or license ID…"
                className="w-full rounded-md border border-border-default bg-surface-1 py-1.5 pl-8 pr-7 text-xs text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
              />
              {search && (
                <button
                  onClick={() => handleSearch('')}
                  aria-label="Clear search"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            <span className="ml-auto text-[11px] tabular-nums text-text-tertiary">
              {totalCount} license{totalCount !== 1 ? 's' : ''}
            </span>
          </div>

          {isLoading ? (
            <SkeletonTable rows={6} />
          ) : totalCount === 0 ? (
            <EmptyState
              icon={<KeyRound />}
              title={allLicenses.length === 0 ? 'No licenses yet' : 'No licenses match'}
              description={allLicenses.length === 0 ? 'Issue your first license to get started' : 'Try a different search'}
              size="sm"
            />
          ) : (
            <>
              <DataTable<LicenseListItem>
                rows={licenses}
                onRowClick={(l) => navigate(`/license/${l.tenantId}`)}
                columns={[
                  {
                    key: 'status',
                    label: 'Status',
                    render: (l) => <LicenseStatusBadge status={l.status} />,
                  },
                  {
                    key: 'id',
                    label: 'License ID',
                    render: (l) => <span className="font-mono text-[11px] text-text-tertiary">#{l.id}</span>,
                  },
                  {
                    key: 'tenantId',
                    label: 'Tenant ID',
                    render: (l) => <span className="font-mono text-[11px] text-accent">{l.tenantId}</span>,
                  },
                  { key: 'tenantName', label: 'Tenant', render: (l) => <span className="text-text-primary">{l.tenantName}</span> },
                  {
                    key: 'licenseKey',
                    label: 'License key',
                    render: (l) => <span className="font-mono text-[11px] text-text-secondary">•••• {l.licenseKeyLastFour}</span>,
                  },
                  {
                    key: 'usage',
                    label: 'Endpoint usage',
                    render: (l) => (
                      <span className="tabular-nums text-text-secondary">{l.endpointsUsed} / {l.endpointLimit}</span>
                    ),
                  },
                  {
                    key: 'expiryDate',
                    label: 'Expires',
                    render: (l) => (
                      <span className="tabular-nums text-[11px] text-text-tertiary">{new Date(l.expiryDate).toLocaleDateString()}</span>
                    ),
                  },
                  {
                    key: 'actions',
                    label: '',
                    width: '72px',
                    render: (l) => (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setTogglingLicense(l); }}
                          aria-label={l.enabled ? 'Disable license' : 'Enable license'}
                          title={l.enabled ? 'Disable license' : 'Enable license'}
                          className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-surface-2 hover:text-text-primary"
                        >
                          {l.enabled ? <PauseCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                        </button>
                        <button
                          onClick={(e) => handleDownload(l, e)}
                          aria-label="Download license file"
                          title="Download license file"
                          className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-surface-2 hover:text-text-primary"
                        >
                          <Download className="h-3 w-3" />
                        </button>
                      </div>
                    ),
                  },
                ]}
              />
              <Pagination page={page} pageSize={PAGE_SIZE} total={totalCount} onPageChange={setPage} />
            </>
          )}
        </Section>
      )}

      <LicenseKeyRevealDialog licenseKey={revealedKey} onClose={() => setRevealedKey(null)} />

      <ConfirmDialog
        open={togglingLicense !== null}
        title={togglingLicense?.enabled ? `Disable ${togglingLicense.tenantName}'s license?` : `Enable ${togglingLicense?.tenantName}'s license?`}
        description={
          togglingLicense?.enabled
            ? 'Endpoints relying on this license may be affected while it is disabled. You can re-enable it at any time.'
            : 'This restores the license to its normal status based on its expiry date.'
        }
        confirmLabel={togglingLicense?.enabled ? 'Disable' : 'Enable'}
        destructive={Boolean(togglingLicense?.enabled)}
        loading={isToggling}
        onConfirm={handleToggleConfirm}
        onCancel={() => setTogglingLicense(null)}
      />
    </div>
  );
}
